import React from 'react';
import { graphql, useFragment } from 'react-relay';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import ArrowRightIconLegacy from '@atlaskit/icon/glyph/arrow-right';
import Lozenge from '@atlaskit/lozenge';
import { Flex, Stack, Text, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import type { pullRequestNeedsWork_periscope_PullRequestNeedsWorkTask$key } from '@atlassian/jira-relay/src/__generated__/pullRequestNeedsWork_periscope_PullRequestNeedsWorkTask.graphql';
import { CommentsIcon, ReviewerIcon } from '../../../common/ui/icons';
import { TaskItem, TaskInfoItem } from '../../../common/ui/task-item';
import { TaskTypeItem } from '../../../common/ui/task-type';
import { useWorkSuggestionsContainer } from '../../../services/periscope-container';
import { useWorkSuggestionsAction } from '../../../services/use-work-suggestions-action';
import { messages } from './messages';

type ItemProps = {
	title: string;
	commentCount: number;
	destinationBranchName?: string | null;
	providerName?: string | null;
	providerIconUrl?: string | null;

	reviewerCount: number;
	repositoryName?: string | null;
};

const Item = ({
	title,
	commentCount,
	destinationBranchName,
	providerName,
	providerIconUrl,
	repositoryName,
	reviewerCount,
}: ItemProps) => (
	<Flex xcss={titleStyles} alignItems="center">
		<Flex xcss={titleStyles} alignItems="center" gap="space.100">
			{providerIconUrl && providerName && (
				<Tooltip content={providerName} position="top">
					<Flex alignItems="center">
						<img src={providerIconUrl} alt={providerName} width="24" height="24" />
					</Flex>
				</Tooltip>
			)}
			<Stack space="space.050">
				<Flex alignItems="center" gap="space.050">
					<Tooltip content={title} position="top">
						<Text color="color.text" maxLines={2}>
							{title}
						</Text>
					</Tooltip>

					{destinationBranchName && (
						<>
							<ArrowRightIcon
								LEGACY_fallbackIcon={ArrowRightIconLegacy}
								LEGACY_size="small"
								label=""
								color={token('color.icon.subtle')}
							/>
							<Tooltip content={destinationBranchName} position="top">
								<Lozenge appearance="default" maxWidth={120}>
									{destinationBranchName}
								</Lozenge>
							</Tooltip>
						</>
					)}
				</Flex>
				<Flex columnGap="space.050" xcss={taskInfoItemsWrapperStyle}>
					<TaskInfoItem Icon={CommentsIcon} text={String(commentCount)} />
					<TaskInfoItem Icon={ReviewerIcon} text={String(reviewerCount)} />
					{repositoryName && <TaskInfoItem text={String(repositoryName)} hasDivider />}
				</Flex>
			</Stack>
		</Flex>
	</Flex>
);

export const PullRequestNeedsWorkTask = ({
	fragmentRef,
}: {
	fragmentRef: pullRequestNeedsWork_periscope_PullRequestNeedsWorkTask$key;
}) => {
	const { formatMessage } = useIntl();

	const data = useFragment(
		graphql`
			fragment pullRequestNeedsWork_periscope_PullRequestNeedsWorkTask on WorkSuggestionsPullRequestNeedsWorkTask {
				__typename
				id
				title
				url
				commentCount
				destinationBranchName
				providerName
				providerIconUrl
				repositoryName
				reviewers {
					__typename
				}
			}
		`,
		fragmentRef,
	);
	const { snooze, remove } = useWorkSuggestionsAction();
	const [{ showReactionsButtonForSuggestions }, { hideSuggestionsReactionsButton }] =
		useWorkSuggestionsContainer();

	return (
		<TaskTypeItem
			onRemove={() => remove({ id: data.id, typename: data.__typename })}
			onSnooze={() => snooze({ id: data.id, typename: data.__typename })}
			showReactionsButton={
				showReactionsButtonForSuggestions[data.id] === true ||
				showReactionsButtonForSuggestions[data.id] == null
			}
			onReactionsButtonClick={() => hideSuggestionsReactionsButton(data.id)}
			url={data.url}
			suggestionType={data.__typename}
			id={data.id}
			actionButtonTitle={formatMessage(messages.actionButtonNonFinal)}
		>
			<TaskItem
				taskInfoContent={
					<Item
						title={data.title}
						commentCount={data.commentCount}
						destinationBranchName={data.destinationBranchName}
						providerName={data.providerName}
						providerIconUrl={data.providerIconUrl}
						reviewerCount={data.reviewers?.length || 0}
						repositoryName={data.repositoryName}
					/>
				}
			/>
		</TaskTypeItem>
	);
};

const taskInfoItemsWrapperStyle = xcss({
	color: 'color.text.subtle',
	lineHeight: '14px',
});

const titleStyles = xcss({
	flex: '4 1 0',
	[media.above.md]: {
		flex: '5 1 0',
	},
});
